import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useMemo, useRef, useState } from "react";
import { type AdminKey, api, type DirectoryEntry, type Me, type UserSummary } from "../api";
import { Dialog, Empty, ErrorMessage, Loading, TagChips } from "../components";
import { count, money, pct, when } from "../format";
import { useForgeEvent } from "../hooks";
import { shortAlias } from "./Keys";
import { LogsPage } from "./Logs";
import { businessUnitName, DivisionPicker, divisionName } from "../DivisionPicker";
import { auditActions, auditActors, type AuditFilter, filterEvents } from "../auditFilters";
import { filterKeys, type KeyFilter, keySearchGroups, ownerLabel, SPEND_FLOORS, UNLABELLED } from "../keyFilters";
import { pick, type PickGroup, type PickOption, pickGrouped } from "../search";
import { SearchPicker } from "../SearchPicker";
import { NO_TEAM, teamLabel, teamOptions } from "../teamFilter";

type Tab = "keys" | "people" | "requests" | "activity";
const TABS: Array<[Tab, string]> = [["keys", "Keys"], ["people", "People"], ["requests", "Requests"], ["activity", "App activity"]];

export function AdminPage({ me }: { me: Me }) {
  const [tab, setTab] = useState<Tab>("keys");
  const bar = useRef<HTMLElement>(null);
  useForgeEvent(bar, "forge-tab-bar-change", (e: CustomEvent<{ index: number }>) => setTab(TABS[e.detail.index]?.[0] ?? "keys"));
  return (
    <>
      <forge-tab-bar ref={bar} active-tab="0">
        {TABS.map(([, label]) => <forge-tab key={label}>{label}</forge-tab>)}
      </forge-tab-bar>
      {tab === "keys" && <AllKeys me={me} />}
      {tab === "people" && <People me={me} />}
      {tab === "requests" && <LogsPage admin />}
      {tab === "activity" && <Activity />}
    </>
  );
}

function AllKeys({ me }: { me: Me }) {
  const keys = useQuery({ queryKey: ["admin-keys"], queryFn: api.adminKeys });
  const [filter, setFilter] = useState<KeyFilter>({ pick: "", division: "", team: "", minSpend: 0, sort: "none" });
  const [retagging, setRetagging] = useState<AdminKey | null>(null);
  const search = useMemo(() => keySearchGroups(keys.data ?? []), [keys.data]);
  if (keys.isPending) return <Loading />;
  if (keys.error) return <ErrorMessage error={keys.error} />;
  const active = keys.data.filter((k) => !k.blocked);
  const labelled = active.filter((k) => k.division);
  const unlabelledSpend = active.filter((k) => !k.division).reduce((s, k) => s + k.spend, 0);
  const rows = filterKeys(keys.data, filter);
  const nextSort = filter.sort === "spend-desc" ? "spend-asc" : filter.sort === "spend-asc" ? "none" : "spend-desc";
  const sortIcon = filter.sort === "spend-desc" ? "arrow_downward" : filter.sort === "spend-asc" ? "arrow_upward" : "unfold_more";

  return (
    <>
      <div className="grid-cards">
        <forge-count-card><forge-icon slot="icon" name="vpn_key" /><span slot="label">Active keys</span><span slot="count">{count(active.length)}</span></forge-count-card>
        <forge-count-card><forge-icon slot="icon" name="tag" /><span slot="label">Labelled with a division</span>
          <span slot="count">{active.length ? pct(labelled.length / active.length) : "–"}</span>
          <span slot="count-end" className="muted">{count(labelled.length)} of {count(active.length)}</span></forge-count-card>
        <forge-count-card><forge-icon slot="icon" name="warning" /><span slot="label">Spend on unlabelled keys (est.)</span><span slot="count">{money(unlabelledSpend)}</span></forge-count-card>
      </div>
      <forge-card>
        <KeyFilterBar me={me} filter={filter} setFilter={setFilter} search={search} teams={teamOptions(keys.data)} shown={rows.length} total={keys.data.length} />
        <div className="table-wrap">
          <table className="forge-data-table">
            <thead><tr><th>Key</th><th>Owner</th><th>Team</th><th>Labels</th><th>Made by</th><th>Last used</th>
              <th className="num" aria-sort={filter.sort === "spend-desc" ? "descending" : filter.sort === "spend-asc" ? "ascending" : "none"}>
                <button type="button" className="sort-button" onClick={() => setFilter((f) => ({ ...f, sort: nextSort }))}>
                  Spend (est.) <forge-icon name={sortIcon} />
                </button>
              </th><th aria-label="Actions" className="cell-sticky-end" /></tr></thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.id}>
                  <td><div>{shortAlias(k)}</div><div className="mono muted">…{k.id.slice(-8)}</div></td>
                  <td className="wrap-anywhere">{k.ownerKind === "person" ? ownerLabel(k)
                    : k.ownerKind === "service" && k.serviceOwner ? <><div>{k.serviceOwner}</div>
                      <div className="muted">service key, backup {k.serviceBackupOwner ?? "not named"}</div></>
                    : <span className="muted">{ownerLabel(k)}</span>}</td>
                  <td className="wrap-anywhere">{teamLabel(k) ?? <span className="muted">No team</span>}</td>
                  <td><TagChips division={k.division} businessUnit={k.businessUnit} /></td>
                  <td>{k.createdByApp ? "This app" : <span className="muted" title="Made in the LiteLLM portal or with its API">LiteLLM</span>}</td>
                  <td className="nowrap">{when(k.lastActive)}</td>
                  <td className="num">{money(k.spend)}</td>
                  <td className="nowrap cell-action cell-sticky-end">
                    <forge-button dense variant="outlined" aria-label={`Retag ${shortAlias(k)}`} disabled={me.readOnly || undefined}
                      onClick={() => setRetagging(k)}>
                      Retag
                    </forge-button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <Empty title="No keys match" message="Clear the search or widen the filters." />}
        </div>
      </forge-card>
      {retagging && <RetagDialog me={me} k={retagging} onClose={() => setRetagging(null)} />}
    </>
  );
}

/**
 * Its own component so its Forge listeners attach when it mounts: the parent renders a skeleton
 * first, and a ref'd select that is not there yet would never get its change listener.
 */
function KeyFilterBar({ me, filter, setFilter, search, teams, shown, total }: {
  me: Me; filter: KeyFilter; setFilter: Dispatch<SetStateAction<KeyFilter>>; search: PickGroup[]; teams: ReturnType<typeof teamOptions>; shown: number; total: number;
}) {
  const searchFilter = useMemo(() => (text: string, value: string | null) => pickGrouped(search, text, value), [search]);
  const divSel = useRef<HTMLElement>(null);
  const teamSel = useRef<HTMLElement>(null);
  const spendSel = useRef<HTMLElement>(null);
  useForgeEvent(divSel, "change", (e) => setFilter((f) => ({ ...f, division: String((e.target as HTMLInputElement).value ?? "") })));
  useForgeEvent(teamSel, "change", (e) => setFilter((f) => ({ ...f, team: String((e.target as HTMLInputElement).value ?? "") })));
  useForgeEvent(spendSel, "change", (e) => setFilter((f) => ({ ...f, minSpend: Number((e.target as HTMLInputElement).value ?? 0) })));
  return (
    <div className="filter-bar mb-medium">
      <SearchPicker id="key-search" label="Search" placeholder="Person or key name" value={filter.pick} filter={searchFilter}
        emptyMessage="Nothing matches" onChange={(v) => setFilter((f) => ({ ...f, pick: v }))} />
      <forge-select label-position="inline-start" ref={divSel} label="Division" value={filter.division} dense style={{ minWidth: "220px" }}>
        <forge-option value="">Any division</forge-option>
        <forge-option value={UNLABELLED}>Unlabelled</forge-option>
        {me.taxonomy.divisions.map((d) => <forge-option key={d.slug} value={d.slug}>{d.name}</forge-option>)}
      </forge-select>
      <forge-select label-position="inline-start" ref={teamSel} label="Team" value={filter.team} dense style={{ minWidth: "220px" }}>
        <forge-option value="">Any team</forge-option>
        <forge-option value={NO_TEAM}>No team</forge-option>
        {teams.map((t) => <forge-option key={t.value} value={t.value}>{t.label}</forge-option>)}
      </forge-select>
      <forge-select label-position="inline-start" ref={spendSel} label="Spend" value={String(filter.minSpend)} dense style={{ minWidth: "160px" }}>
        {SPEND_FLOORS.map((n) => <forge-option key={n} value={String(n)}>{n === 0 ? "Any spend" : `Over ${money(n)}`}</forge-option>)}
      </forge-select>
      <span className="muted">{count(shown)} of {count(total)} keys</span>
    </div>
  );
}

/**
 * Choose a key's division and business unit. When the owner is a person with no directory record,
 * the choice can also become their record; an existing record is only ever changed on People.
 */
function RetagDialog({ me, k, onClose }: { me: Me; k: AdminKey; onClose: () => void }) {
  const qc = useQueryClient();
  const [division, setDivision] = useState(k.suggested?.division ?? "");
  const [bu, setBu] = useState(k.suggested?.businessUnit ?? "");
  const canRecord = k.ownerKind === "person" && !k.ownerHasRecord;
  const [record, setRecord] = useState(canRecord);
  const save = useMutation({
    mutationFn: () => api.adminRetag(k.id, { division, businessUnit: bu, recordForOwner: canRecord && record }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["admin-keys"] }), qc.invalidateQueries({ queryKey: ["admin-users"] })]);
      onClose();
    },
  });
  const person = k.userEmail ?? k.userId;
  return (
    <Dialog open onClose={onClose} label="Retag key">
      <h2 className="forge-typography--heading3 card-title">Retag {shortAlias(k)}</h2>
      <p className="forge-typography--body2">Owner: {ownerLabel(k)}</p>
      <DivisionPicker taxonomy={me.taxonomy} division={division} businessUnit={bu} onChange={(d, b) => { setDivision(d); setBu(b); }} />
      {canRecord && (
        <label className="row gap-xsmall">
          <forge-checkbox checked={record || undefined} onClick={() => setRecord((v) => !v)} />
          Also record this for {person}
        </label>
      )}
      {k.ownerKind === "person" && k.ownerHasRecord && (
        <p className="muted">Changes this key only. {person}'s directory record is not changed; edit it on the People tab.</p>
      )}
      {k.ownerKind === "service" && <p className="muted">Changes this key only. A service key belongs to no person; change its owner on the Service keys page.</p>}
      {k.ownerKind !== "person" && k.ownerKind !== "service" && <p className="muted">Changes this key only: it has no person to record a division against.</p>}
      <p className="muted">Other tags on the key are kept.</p>
      {save.error && <ErrorMessage error={save.error} />}
      <div className="row gap-small">
        <forge-button variant="raised" disabled={!division || !bu || save.isPending || undefined} onClick={() => save.mutate()}>Save</forge-button>
        <forge-button onClick={onClose}>Cancel</forge-button>
      </div>
    </Dialog>
  );
}

function People({ me }: { me: Me }) {
  const users = useQuery({ queryKey: ["admin-users"], queryFn: api.adminUsers });
  const [editing, setEditing] = useState<(UserSummary & { directory: DirectoryEntry | null }) | null>(null);
  if (users.isPending) return <Loading />;
  if (users.error) return <ErrorMessage error={users.error} />;
  const missing = users.data.filter((u) => !u.directory).length;

  return (
    <forge-card>
      <p className="forge-typography--body2 mb-medium">
        {count(users.data.length)} people, {count(missing)} with no division on record.
      </p>
      <div className="table-wrap">
        <table className="forge-data-table">
          <thead><tr><th>Person</th><th>Role</th><th>Division</th><th>Business unit</th><th>Source</th><th className="num">Spend (est.)</th><th /></tr></thead>
          <tbody>
            {users.data.map((u) => (
              <tr key={u.userId}>
                <td>{u.email ?? u.userId}</td>
                <td>{u.role === "proxy_admin" ? "Administrator" : u.role}</td>
                <td>{u.directory ? divisionName(me.taxonomy, u.directory.division) : <span className="muted">not recorded</span>}</td>
                <td>{u.directory ? businessUnitName(me.taxonomy, u.directory.businessUnit) : ""}</td>
                <td className="muted">{u.directory ? `${u.directory.source}, ${when(u.directory.verifiedAt)}` : ""}</td>
                <td className="num">{money(u.spend)}</td>
                <td><forge-button dense variant="outlined" disabled={me.readOnly || undefined} onClick={() => setEditing(u)}>Edit</forge-button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <EditDirectory me={me} user={editing} onClose={() => setEditing(null)} />}
    </forge-card>
  );
}

function EditDirectory({ me, user, onClose }: { me: Me; user: UserSummary & { directory: DirectoryEntry | null }; onClose: () => void }) {
  const qc = useQueryClient();
  const [division, setDivision] = useState(user.directory?.division ?? "");
  const [bu, setBu] = useState(user.directory?.businessUnit ?? "");
  const save = useMutation({
    mutationFn: () => api.adminSetDirectory(user.userId, { division, businessUnit: bu, email: user.email }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["admin-users"] }); onClose(); },
  });
  return (
    <Dialog open onClose={onClose} label="Edit directory record">
      <h2 className="forge-typography--heading3 card-title">{user.email ?? user.userId}</h2>
      <DivisionPicker taxonomy={me.taxonomy} division={division} businessUnit={bu} onChange={(d, b) => { setDivision(d); setBu(b); }} />
      <p className="muted">Existing keys keep their old labels until retagged from the Keys tab.</p>
      {save.error && <ErrorMessage error={save.error} />}
      <div className="row gap-small">
        <forge-button variant="raised" disabled={!division || !bu || save.isPending || undefined} onClick={() => save.mutate()}>Save</forge-button>
        <forge-button onClick={onClose}>Cancel</forge-button>
      </div>
    </Dialog>
  );
}

function Activity() {
  const events = useQuery({ queryKey: ["admin-audit"], queryFn: api.adminAudit });
  const [filter, setFilter] = useState<AuditFilter>({ who: "", action: "" });
  const actors = useMemo(() => auditActors(events.data ?? []), [events.data]);
  if (events.isPending) return <Loading />;
  if (events.error) return <ErrorMessage error={events.error} />;
  const rows = filterEvents(events.data, filter);
  return (
    <forge-card>
      <p className="forge-typography--body2 mb-medium">Everything this app did, newest first (the latest {count(events.data.length)} events).</p>
      <ActivityFilterBar actions={auditActions(events.data)} actors={actors} filter={filter} setFilter={setFilter} shown={rows.length} total={events.data.length} />
      <div className="table-wrap">
        <table className="forge-data-table">
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
          <tbody>
            {rows.map((e, i) => (
              <tr key={`${e.at}-${i}`}>
                <td className="nowrap">{when(e.at)}</td>
                <td>{e.actor}</td>
                <td className="mono">{e.action}</td>
                <td className="mono">{e.target ? (e.target.length > 40 ? `…${e.target.slice(-8)}` : e.target) : ""}</td>
                <td className="mono muted">{JSON.stringify(e.detail).slice(0, 120)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <Empty title="No activity matches" message="Clear the search or choose another action." />}
      </div>
    </forge-card>
  );
}

/** Mounted after the events load, so the select's listener attaches (see KeyFilterBar). */
function ActivityFilterBar({ actions, actors, filter, setFilter, shown, total }: {
  actions: string[]; actors: PickOption[]; filter: AuditFilter; setFilter: Dispatch<SetStateAction<AuditFilter>>; shown: number; total: number;
}) {
  const whoFilter = useMemo(() => (text: string, value: string | null) => pick(actors, text, value), [actors]);
  const actionSel = useRef<HTMLElement>(null);
  useForgeEvent(actionSel, "change", (e) => setFilter((f) => ({ ...f, action: String((e.target as HTMLInputElement).value ?? "") })));
  return (
    <div className="filter-bar mb-medium">
      <SearchPicker id="activity-who" label="Who" placeholder="Anyone" value={filter.who} filter={whoFilter}
        onChange={(v) => setFilter((f) => ({ ...f, who: v }))} />
      <forge-select label-position="inline-start" ref={actionSel} label="Action" value={filter.action} dense style={{ minWidth: "220px" }}>
        <forge-option value="">Any action</forge-option>
        {actions.map((a) => <forge-option key={a} value={a}>{a}</forge-option>)}
      </forge-select>
      <span className="muted">{count(shown)} of {count(total)} events</span>
    </div>
  );
}
